'use client';

import { useEffect } from 'react';
import { HERO_WASH_GRADIENT } from '@/shared/ui';
import { ProjectActionsMenu } from './ProjectActionsMenu';
import { ProjectSwitcher } from './ProjectSwitcher';
import { BuildingPackTag } from './BuildingPackTag';
import { useProjects } from '../hooks/use-projects';
import { useOptionalDesktopLayout } from '../context/DesktopLayoutContext';

interface ProjectHeroSectionProps {
  projectName: string;
  projectId?: string;
  // Center the hero title (+ switcher chevron) horizontally. Set only in the
  // onboarding empty state, where the placeholder below is centered; left flag
  // (default) once the building has content.
  isCentered?: boolean;
}

export function ProjectHeroSection({
  projectName,
  projectId,
  isCentered = false,
}: ProjectHeroSectionProps) {
  // Per-building pack marking on the building's own page (mirrors the storefront
  // list). Pack state rides on the already-loaded projects (server-computed,
  // never derived from purchases). The marking is shown ONLY for an active
  // Kit/Pro Plan pack — a positive status badge / reward. The no-pack ("Free")
  // state is deliberately UNMARKED on the building hero: a fresh building reads
  // as "just my house", with zero upgrade nag in the activation funnel. Users
  // who want to buy reach the pack paywall from the Account page. (The shared
  // BuildingPackTag still renders the muted "Free" state in the Packs storefront
  // list, where the marking is purely informational.)
  const { data: projectsResponse } = useProjects();
  const pack = projectsResponse?.data.find((p) => p.id === projectId)?.attributes
    .pack;
  const isPackActive = pack?.is_active === true;
  // Tell the desktop shell to paint the hero wash full-bleed behind both
  // columns (so it flows under the frosted-glass sidebar). Cleared on unmount,
  // so concept/editor pages — which don't render this hero — leave it off.
  // Safe no-op on the mobile layout (no shell), where this header keeps its
  // own in-content wash below.
  const setShowHeroWash =
    useOptionalDesktopLayout()?.setShowHeroWash;
  useEffect(() => {
    setShowHeroWash?.(true);
    return () => setShowHeroWash?.(false);
  }, [setShowHeroWash]);

  return (
    // Apple Health-style header (mirrors the Apple `ProjectHeroSection`): a
    // large leading title over a soft teal wash that fades into the page
    // background. No house photo, no subtitle. The wash uses the brand teal
    // (H=177°, S=90%) in the light shade tuned on Apple — top → transparent.
    <header className="relative w-full">
      {/* In-content wash for the mobile layout only. At `lg+` the desktop
          shell paints the same wash full-bleed behind the columns (flowing
          under the sidebar), so this one is hidden to avoid doubling up. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[180%] lg:hidden"
        style={{ background: HERO_WASH_GRADIENT }}
      />

      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Floating controls. The building page is the app home, so there is no
            back button — only the trailing ⋮ menu (rename / delete /
            create-building). The building switcher now lives in the title
            below. */}
        <div className="flex items-center justify-end pt-3 min-h-9">
          <ProjectActionsMenu variant="hero" />
        </div>

        {/* Large leading title (dark), aligned with the content sections below.
            When there is more than one building the title doubles as the
            building switcher (title + up/down chevron); a single building shows
            a plain title. Mirrors the Apple hero. */}
        <ProjectSwitcher
          currentProjectId={projectId}
          currentProjectName={projectName}
          isCentered={isCentered}
        />

        {/* Pack marking for this building — only the active Kit/Pro Plan badge.
            The no-pack "Free" state is intentionally unmarked here (see comment
            above); the purchase funnel lives on the Account page. */}
        {isPackActive && (
          <div className="-mt-2 flex flex-wrap items-center gap-3 pb-6">
            <BuildingPackTag pack={pack} />
          </div>
        )}
      </div>
    </header>
  );
}
